import { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import { AuthEntryCard } from '@/features/auth/components/AuthEntryCard'
import { BiometricPreview } from '@/features/auth/components/BiometricPreview'
import { RegistrationBackButton } from '@/features/auth/components/RegistrationBackButton'
import { Button } from '@/shared/components/ui/Button'
import { registrationService } from '@/features/auth/services/registration.service'
import {
  getRegistrationState,
  markBiometricCompleted,
  setIdentityData,
  setFlowStep,
} from '@/features/auth/store/registration.store'

type BiometricPhase = 'ready' | 'verifying' | 'completed'

const BIOMETRIC_TIMEOUT_MS = 600_000
const POLLING_INTERVAL_MS = 3_000
const LANDSCAPE_MOBILE_QUERY = '(max-width: 1024px) and (orientation: landscape) and (pointer: coarse)'

const NEGATIVE_STATES = new Set(['failed', 'not_found'])
const FOREIGNER_BIOMETRY_BYPASS_CODE = 'BIOMETRY_FOREIGNER_BYPASS'

export function AuthBiometricVerificationPage() {
  const navigate = useNavigate()
  const { t } = useTranslation('register')
  const timersRef = useRef<number[]>([])
  const pollCountRef = useRef(0)
  const [phase, setPhase] = useState<BiometricPhase>('ready')
  const [statusMessage, setStatusMessage] = useState(
    t('register:biometric.status.face'),
  )
  const [biometricIframeUrl, setBiometricIframeUrl] = useState<string | null>(null)
  const [showSuccessModal, setShowSuccessModal] = useState(false)
  const [showErrorModal, setShowErrorModal] = useState(false)
  const [attemptCount, setAttemptCount] = useState(0)
  const [isPortraitMobile, setIsPortraitMobile] = useState(() => {
    if (typeof window === 'undefined') return true
    return !window.matchMedia(LANDSCAPE_MOBILE_QUERY).matches
  })

  const { biometricToken: guardToken } = getRegistrationState()

  const progressValue = useMemo(() => {
    switch (phase) {
      case 'verifying': return 50
      case 'completed': return 100
      default: return 0
    }
  }, [phase])

  useEffect(() => {
    if (!guardToken) {
      navigate('/register/identity', { replace: true })
    }
  }, [guardToken, navigate])

  useEffect(() => {
    if (guardToken) {
      setFlowStep('biometric')
    }
  }, [guardToken])

  useEffect(() => {
    return () => {
      timersRef.current.forEach((timer) => window.clearTimeout(timer))
    }
  }, [])

  useEffect(() => {
    if (typeof window === 'undefined') return

    const mediaQuery = window.matchMedia(LANDSCAPE_MOBILE_QUERY)
    const updateOrientationState = () => {
      setIsPortraitMobile(!mediaQuery.matches)
    }

    updateOrientationState()
    mediaQuery.addEventListener('change', updateOrientationState)

    return () => {
      mediaQuery.removeEventListener('change', updateOrientationState)
    }
  }, [])

  if (!guardToken) return null

  function clearPendingTimers() {
    timersRef.current.forEach((timer) => window.clearTimeout(timer))
    timersRef.current = []
  }

  function startPolling() {
    if (!isPortraitMobile) {
      setStatusMessage(t('register:biometric.orientation.description'))
      return
    }

    const {
      biometricToken,
      biometricCodigoId,
      biometricCorrelationId,
      biometricRequestId,
      biometricProcessId,
      correlationId,
    } = getRegistrationState()

    if (!biometricToken || !biometricCodigoId) return

    const token: string = biometricToken
    const codigoId: string = biometricCodigoId

    setBiometricIframeUrl(`https://facial-recognition.geektech.com.ec:6443/?token=${token}`)
    setPhase('verifying')
    setStatusMessage(t('register:biometric.status.verifying'))
    pollCountRef.current = 0

    const totalPolls = Math.floor(BIOMETRIC_TIMEOUT_MS / POLLING_INTERVAL_MS)

    const payload = {
      token,
      codigoId,
      correlationId: biometricCorrelationId ?? '',
      requestId: biometricRequestId ?? '',
    }

    async function continueWithoutBiometry() {
      if (!correlationId) return

      const bypassResult = await registrationService.startWithoutBiometry(correlationId, getRegistrationState().documentNumber)
      if (bypassResult.success) {
        setIdentityData(getRegistrationState().documentNumber, getRegistrationState().dactilar)
        markBiometricCompleted()
        navigate('/register/otp', { replace: true })
        return
      }

      setShowErrorModal(true)
    }

    function poll() {
      if (pollCountRef.current >= totalPolls) {
        setShowErrorModal(true)
        return
      }

      pollCountRef.current += 1

      const timer = window.setTimeout(async () => {
        const result = await registrationService.consultarEstadoBiometria(payload)

        if (!result.success) {
          if (result.errorCode === FOREIGNER_BIOMETRY_BYPASS_CODE) {
            await continueWithoutBiometry()
            return
          }

          const isTerminal =
            result.errorCode === 'BIOMETRY_RESULT_NOT_FOUND' ||
            result.errorCode === 'BIOMETRY_EXTERNAL_SERVICE_ERROR'
          if (isTerminal) {
            setShowErrorModal(true)
            return
          }
          poll()
          return
        }

        const { canContinueRegistration, state } = result.data

        if (canContinueRegistration) {
          if (correlationId) {
            const biometryPayload = {
              biometria: {
                token,
                processId: biometricProcessId,
                codigoId,
                correlationId: biometricCorrelationId ?? '',
                requestId: biometricRequestId ?? '',
              },
            }

            const maxAttempts = 3
            const delayMs = 3000
            let biometryResult = null

            for (let attempt = 1; attempt <= maxAttempts; attempt++) {
              await new Promise(resolve => setTimeout(resolve, delayMs))
              biometryResult = await registrationService.startWithBiometry(correlationId, biometryPayload)
              if (biometryResult.success) break
            }

            if (!biometryResult?.success) {
              if (biometryResult?.errorCode === FOREIGNER_BIOMETRY_BYPASS_CODE) {
                await continueWithoutBiometry()
                return
              }

              setShowErrorModal(true)
              return
            }
          }
          markBiometricCompleted()
          setFlowStep('otp-contact')
          setPhase('completed')
          setStatusMessage(t('register:biometric.status.completed'))
          setShowSuccessModal(true)
          return
        }

        if (NEGATIVE_STATES.has(state)) {
          const next = attemptCount + 1
          setAttemptCount(next)
          if (next >= 3) {
            navigate('/register/consent', { replace: true })
            return
          }
          setShowErrorModal(true)
          return
        }

        poll()
      }, POLLING_INTERVAL_MS)

      timersRef.current.push(timer)
    }

    poll()
  }

  function resetBiometricFlow() {
    clearPendingTimers()
    setShowErrorModal(false)
    navigate('/register/identity', { replace: true })
  }

  return (
    <>
      <AuthEntryCard
        variant="biometric"
        contentClassName="text-center"
      >
        <section className="mt-4 w-full max-w-96 text-center">
          <h1 className="m-0 text-xl leading-tight font-bold text-text-primary">{t('register:biometric.title')}</h1>
        </section>

        {phase === 'ready' ? (
          <div className="mt-8 flex w-full max-w-[32rem] flex-col items-center">
            <div className="w-full rounded-xl bg-[#eef7fc] px-5 py-4 text-center">
              <p className="text-sm font-semibold leading-5 text-text-primary">{t('register:biometric.caution.title')}</p>
              <p className="mt-2 text-sm leading-6 text-text-muted">
                {t('register:biometric.caution.text')}
              </p>
            </div>

            <p className="mt-6 text-center text-sm leading-6 text-text-muted">{statusMessage}</p>

            {!isPortraitMobile ? (
              <div className="mt-4 w-full rounded-xl border border-[#f5c2c7] bg-[#fde9eb] px-5 py-4 text-center">
                <p className="text-sm font-semibold leading-5 text-text-primary">
                  {t('register:biometric.orientation.title')}
                </p>
                <p className="mt-2 text-sm leading-6 text-text-muted">
                  {t('register:biometric.orientation.description')}
                </p>
              </div>
            ) : null}

            <div className="mt-6">
              <BiometricPreview isReady />
            </div>

            <div className="mt-8 flex w-full max-w-96 items-center justify-center gap-3">
              <RegistrationBackButton />
              <Button className="h-9 w-28 px-5 py-1.5 text-sm" onClick={startPolling}>
                {t('register:biometric.btn.start')}
              </Button>
            </div>
          </div>
        ) : null}

        {phase === 'verifying' && !showSuccessModal ? (
          <div className="mt-8 flex w-full max-w-[40rem] flex-col items-center">
            {!isPortraitMobile && phase === 'verifying' ? (
              <div className="w-full rounded-xl border border-[#f5c2c7] bg-[#fde9eb] px-5 py-4 text-center">
                <p className="text-sm font-semibold leading-5 text-text-primary">
                  {t('register:biometric.orientation.title')}
                </p>
                <p className="mt-2 text-sm leading-6 text-text-muted">
                  {t('register:biometric.orientation.description')}
                </p>
              </div>
            ) : null}

            {biometricIframeUrl && phase === 'verifying' && isPortraitMobile ? (
              <div className="h-[580px] w-full overflow-hidden rounded-xl border border-border-soft bg-white shadow-sm">
                <iframe
                  allow="camera *; microphone *; geolocation *"
                  className="block h-[calc(580px/0.9)] w-[calc(100%/0.9)] origin-top-left scale-[0.9] border-0 bg-transparent"
                  src={biometricIframeUrl}
                  title={t('register:biometric.iframe.title')}
                />
              </div>
            ) : null}

            <p className="mt-6 text-center text-sm leading-6 text-text-muted">{statusMessage}</p>

            <div
              className="mt-4 h-2 w-full overflow-hidden rounded-full bg-[#e5edf4]"
              aria-label={t('register:biometric.progress.label')}
              aria-valuemax={100}
              aria-valuemin={0}
              aria-valuenow={progressValue}
              role="progressbar"
            >
              <span className="block h-full rounded-full bg-brand-primary transition-[width] duration-200" style={{ width: `${progressValue}%` }} />
            </div>
          </div>
        ) : null}
      </AuthEntryCard>

      {showSuccessModal ? (
        <div className="fixed inset-0 z-40 flex items-center justify-center px-4" role="dialog" aria-modal="true">
          <div className="absolute inset-0 bg-[#00000033]" />
          <div className="relative z-10 flex w-full max-w-sm flex-col items-center rounded-md bg-white px-6 py-8 text-center shadow-xl">
            <div className="flex h-8 w-8 items-center justify-center rounded-full border-2 border-[#1FC16B] text-sm font-bold text-[var(--color-success,#22c55e)]" aria-hidden="true">✓</div>
            <h2 className="mt-4 text-[26px] font-bold leading-tight text-text-primary">{t('register:biometric.success.title')}</h2>
            <p className="mt-3 text-[16px] leading-6 text-text-muted">
              {t('register:biometric.success.description')}
            </p>
            <div className="mt-8 w-full">
              <Button
                className="min-h-9 !bg-[#1FC16B] px-5 py-1.5 text-sm !text-white hover:!bg-[#1ead5b] active:!bg-[#19994f]"
                onClick={() => navigate('/register/otp', { replace: true })}
              >
                {t('register:biometric.success.btn')}
              </Button>
            </div>
          </div>
        </div>
      ) : null}

      {showErrorModal ? (
        <div className="fixed inset-0 z-40 flex items-center justify-center px-4" role="dialog" aria-modal="true">
          <div className="absolute inset-0 bg-[rgba(5,20,31,0.55)]" />
          <div className="relative z-10 flex w-full max-w-sm flex-col items-center rounded-md bg-white px-6 py-8 text-center shadow-xl">
            <div
              className="flex h-14 w-14 items-center justify-center rounded-full bg-[#ffebee] text-2xl font-bold text-[#ff384d]"
              aria-hidden="true"
            >
              !
            </div>
            <h2 className="mt-4 text-base font-bold leading-5 text-text-primary">{t('register:biometric.error.title')}</h2>
            <p className="mt-3 text-sm leading-6 text-text-muted">
              {t('register:biometric.error.description')}
            </p>
            <div className="mt-8 w-full">
              <Button
                className="min-h-9 bg-[#ff384d] px-5 py-1.5 text-sm hover:bg-[#e03245] active:bg-[#c92d3e]"
                onClick={resetBiometricFlow}
              >
                {t('register:biometric.error.btn')}
              </Button>
            </div>
          </div>
        </div>
      ) : null}
    </>
  )
}
